import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice, CONTACT } from "@/lib/constants";
import { typeLabel } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, MessageCircle, Heart, Share2, MapPin, Eye, Copy } from "lucide-react";
import MapPicker from "@/components/MapPicker";
import BackButton from "@/components/BackButton";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [i, setI] = useState(0);
  const [fav, setFav] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => { setP(r.data); setFav(!!r.data.is_favorite); }).catch(() => nav("/properties"));
  }, [id, nav]);

  if (!p) return <div className="p-16 text-center text-slate-500">{t.common.loading}</div>;
  const imgs = p.images && p.images.length > 0 ? p.images : [];
  const current = imgs[i] ? fileUrl(imgs[i]) : "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80";

  const toggleFav = async () => {
    if (!user) { toast.info(t.auth.loginNeeded); nav("/login"); return; }
    if (fav) { await api.delete(`/favorites/${p.id}`); setFav(false); }
    else { await api.post(`/favorites/${p.id}`); setFav(true); }
  };
  const share = async () => {
    try { await navigator.share({ title: p.title, url: window.location.href }); }
    catch { await navigator.clipboard.writeText(window.location.href); toast.success(t.detail.copyLink); }
  };
  const phone = p.show_phone ? (p.contact_phone || CONTACT.phone) : "";
  const wa = p.show_whatsapp ? (p.contact_whatsapp || CONTACT.whatsapp).replace(/^00/, "") : "";

  // smart back: prefer state.from (list query) then history back
  const backTo = loc.state?.from || -1;
  const backLabel = loc.state?.from ? t.detail.back : t.common.back;

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      <div className="mb-3">
        <BackButton to={backTo} label={backLabel} />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10]">
            <img src={current} alt={p.title} className="w-full h-full object-cover" />
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-2 fl-scroll overflow-x-auto pb-2">
              {imgs.map((im, idx) => (
                <button key={idx} onClick={() => setI(idx)} data-testid={`gallery-thumb-${idx}`}
                  className={`shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 ${idx === i ? "border-emerald-900" : "border-transparent"}`}>
                  <img src={fileUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-emerald-900 text-white hover:bg-emerald-900">{typeLabel(p.type, lang)}</Badge>
              {p.featured && <Badge className="bg-amber-500 text-white hover:bg-amber-500">{lang === "ar" ? "مميز" : "Featured"}</Badge>}
              <div className="text-xs text-slate-500 flex items-center gap-1 ms-auto"><Eye className="w-3.5 h-3.5" /> {p.views}</div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-emerald-950 mt-3">{p.title}</h1>
            <div className="text-sm text-slate-600 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" /> {p.governorate}{p.city ? ` · ${p.city}` : ""}{p.area ? ` · ${p.area}` : ""}</div>
          </div>

          {Object.keys(p.details || {}).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-bold text-emerald-950 mb-3">{t.detail.spec}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                {Object.entries(p.details).map(([k, v]) => v ? (
                  <div key={k} className="flex flex-col">
                    <span className="text-xs text-slate-500">{t.fields[k] || k}</span>
                    <span className="font-semibold text-emerald-950 fl-num">{typeof v === "number" ? formatPrice(v) : v}</span>
                  </div>
                ) : null)}
              </div>
            </div>
          )}

          {p.description && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="font-bold text-emerald-950 mb-2">{t.detail.desc}</h3>
              <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{p.description}</p>
            </div>
          )}

          {p.lat && p.lng && (
            <div>
              <h3 className="font-bold text-emerald-950 mb-2">{t.detail.location}</h3>
              <MapPicker value={{ lat: p.lat, lng: p.lng }} interactive={false} height={280} />
            </div>
          )}

          <div className="text-xs text-slate-500 space-y-1 pt-2 border-t border-slate-200">
            <div>· {t.detail.trustA}</div>
            <div>· {t.detail.trustB}</div>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-20">
            <div className="text-xs text-slate-500">{t.detail.price}</div>
            <div className="fl-num text-3xl font-extrabold text-emerald-950 mt-1">{formatPrice(p.price)} <span className="text-sm text-slate-500 font-normal">{t.common.currency}</span></div>
            {p.price_negotiable && <div className="text-xs text-amber-700 mt-1">{t.detail.negotiable}</div>}

            <div className="mt-5 space-y-2">
              {phone && (
                <a href={`tel:${phone}`} className="w-full">
                  <Button data-testid="contact-call" className="w-full bg-emerald-900 hover:bg-emerald-950 h-12 gap-2"><Phone className="w-4 h-4" /> {t.detail.call} {phone}</Button>
                </a>
              )}
              {wa && (
                <a href={`https://wa.me/${wa}?text=${encodeURIComponent(p.title + " - " + window.location.href)}`} target="_blank" rel="noreferrer" className="w-full">
                  <Button data-testid="contact-wa" className="w-full bg-green-600 hover:bg-green-700 h-12 gap-2"><MessageCircle className="w-4 h-4" /> {t.detail.whatsapp}</Button>
                </a>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={toggleFav} className="flex-1 gap-2" data-testid="detail-fav">
                  <Heart className="w-4 h-4" fill={fav ? "currentColor" : "none"} /> {fav ? t.detail.saved : t.detail.save}
                </Button>
                <Button variant="outline" onClick={share} className="flex-1 gap-2"><Share2 className="w-4 h-4" /> {t.detail.share}</Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success(t.detail.copyLink); }} className="w-full gap-2 text-slate-600"><Copy className="w-3.5 h-3.5" /> {t.detail.copyLink}</Button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
